import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBizDashboard } from '@hooks';

const STAT_CARDS = [
    { key: 'total_cards',    label: '전체 명함',  colorClass: 'bg-blue-50 text-blue-700 border-blue-200',       format: (v) => v ?? '-' },
    { key: 'pending_review', label: '검수 대기',  colorClass: 'bg-yellow-50 text-yellow-700 border-yellow-200', format: (v) => v ?? '-' },
    { key: 'completed',      label: '확정 완료',  colorClass: 'bg-green-50 text-green-700 border-green-200',    format: (v) => v ?? '-' },
    { key: 'average_ocr',    label: 'OCR 신뢰도', colorClass: 'bg-gray-50 text-gray-700 border-gray-200',      format: (v) => v != null ? `${Math.round(v * 100)}%` : '-' },
];

const FLOW_STEPS = ['이미지 업로드', 'OCR 실행', 'LLM 구조화', '관리자 검수', '최종 확정 저장'];

const STATUS_BADGE = {
    pending_review: { bg: '#fef9c3', color: '#92400e', label: '검수 대기' },
    completed:      { bg: '#dcfce7', color: '#166534', label: '확정 완료' },
    ocr_failed:     { bg: '#fee2e2', color: '#991b1b', label: 'OCR 오류' },
    uploaded:       { bg: '#f3f4f6', color: '#4b5563', label: '업로드됨' },
    ocr_completed:  { bg: '#dbeafe', color: '#1d4ed8', label: 'OCR 완료' },
};

export function BizDashboard() {
    const navigate = useNavigate();
    const { summary, recentItems, isLoading, isError, refetch } = useBizDashboard();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const fn = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', fn);
        return () => window.removeEventListener('resize', fn);
    }, []);

    return (
        <div className="p-4 max-w-5xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-gray-800">명함 대시보드</h2>
                <button
                    onClick={() => refetch()}
                    className="text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded px-3 py-1"
                >
                    새로고침
                </button>
            </div>

            {isError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-600">
                    요약 데이터를 불러오지 못했습니다. BizScanPro 서버 연결을 확인해주세요.
                </div>
            )}

            {/* 통계 카드 4개 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {STAT_CARDS.map(({ key, label, colorClass, format }) => (
                    <div key={key} className={`rounded-xl border p-4 flex flex-col gap-1 ${colorClass}`}>
                        <span className="text-xs font-medium opacity-80">{label}</span>
                        {isLoading ? (
                            <div className="h-7 w-12 bg-current opacity-20 rounded animate-pulse" />
                        ) : (
                            <span className="text-2xl font-bold">
                                {format(summary?.[key] ?? null)}
                            </span>
                        )}
                    </div>
                ))}
            </div>

            {/* 처리 흐름 + 서버 연동 상태 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">

                {/* 처리 흐름 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700">처리 흐름</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">실행 중</span>
                    </div>
                    <ol className="space-y-2">
                        {FLOW_STEPS.map((step, i) => (
                            <li key={step} className="flex items-center gap-2 text-sm text-gray-600">
                                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0">
                                    {i + 1}
                                </span>
                                {step}
                            </li>
                        ))}
                    </ol>
                </div>

                {/* 서버 연동 상태 */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700">서버 연동 상태</h3>
                        <span className="text-xs text-gray-400">API 기반</span>
                    </div>
                    <div className="space-y-2">
                        {[
                            ['대시보드 API', isError ? '오류' : '정상',      isError ? 'text-red-500' : 'text-green-600'],
                            ['최근 항목 수', String(recentItems.length),     'text-gray-700'],
                            ['데이터 소스',  'Backend API',                  'text-gray-700'],
                        ].map(([label, value, cls]) => (
                            <div key={label} className="flex justify-between items-center text-sm">
                                <span className="text-gray-500">{label}</span>
                                <strong className={cls}>{value}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 최근 명함 */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700">최근 명함</h3>
                    <span className="text-xs text-gray-400">서버 조회</span>
                </div>

                {isLoading ? (
                    <p className="py-6 text-center text-gray-400 text-xs">로딩 중...</p>
                ) : recentItems.length === 0 ? (
                    <p className="py-6 text-center text-gray-400 text-xs">표시할 데이터가 없습니다.</p>
                ) : isMobile ? (
                    /* 모바일: 카드 리스트 */
                    <div className="flex flex-col gap-2">
                        {recentItems.map((item) => {
                            const badge = STATUS_BADGE[item.status] ?? null;
                            return (
                                <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 gap-2">
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                        <span className="text-xs text-gray-700 font-medium truncate">{item.filename}</span>
                                        <span className="text-xs text-gray-400">{item.created_at}</span>
                                    </div>
                                    {badge ? (
                                        <span
                                            className="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0"
                                            style={{ background: badge.bg, color: badge.color }}
                                        >
                                            {badge.label}
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-400 shrink-0">{item.status}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* 데스크탑: 테이블 */
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="text-left text-xs font-semibold text-gray-400 pb-2 pr-4">ID</th>
                                    <th className="text-left text-xs font-semibold text-gray-400 pb-2 pr-4">파일명</th>
                                    <th className="text-left text-xs font-semibold text-gray-400 pb-2 pr-4">상태</th>
                                    <th className="text-left text-xs font-semibold text-gray-400 pb-2">생성 시각</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentItems.map((item) => {
                                    const badge = STATUS_BADGE[item.status] ?? null;
                                    return (
                                        <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50">
                                            <td className="py-2 pr-4 text-gray-500 text-xs font-mono">{item.id}</td>
                                            <td className="py-2 pr-4 text-gray-700 text-xs truncate" style={{ maxWidth: '180px' }}>{item.filename}</td>
                                            <td className="py-2 pr-4">
                                                {badge ? (
                                                    <span
                                                        className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                                        style={{ background: badge.bg, color: badge.color }}
                                                    >
                                                        {badge.label}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">{item.status}</span>
                                                )}
                                            </td>
                                            <td className="py-2 text-gray-500 text-xs">{item.created_at}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* 액션 버튼 */}
            <div className="flex flex-wrap gap-3">
                <button
                    onClick={() => navigate('/biz/card-list')}
                    className="bg-gray-600 text-white px-4 py-2 rounded text-sm hover:bg-gray-700"
                >
                    명함 목록 보기
                </button>
                <button
                    onClick={() => navigate('/biz/card-upload')}
                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
                >
                    + 명함 업로드
                </button>
            </div>
        </div>
    );
}
